import { useCallback, useMemo, useRef, useState } from 'react';

import DatePicker from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { FormList, FormListFieldError, FormListItem } from '../../../../components/FormList';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { WorkflowSubStepProps } from '../../config';

export interface MilitaryExperienceEntry {
  branch: string;
  dateServedFrom: string;
  dateServedTo: string;
  dischargeType: string;
}

export interface MilitaryExperienceFormData {
  isAddingEntry?: boolean;
  hasMilitaryExperience: boolean | null;
  militaryExperiences: FormListItem<MilitaryExperienceEntry>[];
  draftMilitaryExperience: MilitaryExperienceEntry;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const militaryBranchOptions = [
  { label: '- Select an option -', value: '' },
  { label: 'Air Force', value: 'air-force' },
  { label: 'Army', value: 'army' },
  { label: 'Coast Guard', value: 'coast-guard' },
  { label: 'Marine Corps', value: 'marine-corps' },
  { label: 'Navy', value: 'navy' },
  { label: 'Space Force', value: 'space-force' },
];

const dischargeTypeOptions = [
  { label: '- Select an option -', value: '' },
  { label: 'Honorable', value: 'honorable' },
  { label: 'General (Under Honorable Conditions)', value: 'general' },
  { label: 'Other Than Honorable', value: 'other-than-honorable' },
  { label: 'Bad Conduct', value: 'bad-conduct' },
  { label: 'Dishonorable', value: 'dishonorable' },
  { label: 'Entry Level Separation', value: 'entry-level' },
];

const MILITARY_DATE_RANGE_ERROR = 'The "To" date must be after the "From" date.';

const getEmptyMilitaryExperience = (): MilitaryExperienceEntry => ({
  branch: '',
  dateServedFrom: '',
  dateServedTo: '',
  dischargeType: '',
});

function MilitaryExperiencePage({ data, updateData, fieldErrors }: WorkflowSubStepProps<MilitaryExperienceFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'military-experience-page';

  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 100);

  const handleMilitaryExperienceChange = (value: boolean | null) => {
    // If changing from Yes to No, clear the military experiences
    if (value !== true) {
      updateData({
        ...data,
        draftMilitaryExperience: getEmptyMilitaryExperience(),
        hasMilitaryExperience: value,
        isAddingEntry: false,
        militaryExperiences: [],
      });
    } else {
      updateData({
        ...data,
        hasMilitaryExperience: value,
      });
    }
  };

  const pendingItemsRef = useRef<FormListItem<MilitaryExperienceEntry>[] | null>(null);

  const handleItemsChange = useCallback(
    (items: FormListItem<MilitaryExperienceEntry>[]) => {
      pendingItemsRef.current = items;
      updateData({
        ...data,
        militaryExperiences: items,
      });
    },
    [data, updateData],
  );

  const handleDraftChange = useCallback(
    (draft: MilitaryExperienceEntry, isDraftActive?: boolean) => {
      const itemsToUse = pendingItemsRef.current !== null ? pendingItemsRef.current : data.militaryExperiences;
      pendingItemsRef.current = null;

      updateData({
        ...data,
        draftMilitaryExperience: draft,
        isAddingEntry: isDraftActive ?? data.isAddingEntry,
        militaryExperiences: itemsToUse,
      });
    },
    [data, updateData],
  );

  const handleDraftActiveChange = useCallback(
    (isActive: boolean) => updateData({ ...data, isAddingEntry: isActive }),
    [data, updateData],
  );

  const validateMilitaryExperience = useCallback((item: MilitaryExperienceEntry): FormListFieldError => {
    const errors: FormListFieldError = {};

    if (!item.branch) {
      errors.branch = 'Branch of the Military is required';
    }
    if (!item.dateServedFrom) {
      errors.dateServedFrom = 'Date Served From is required';
    }
    if (!item.dateServedTo) {
      errors.dateServedTo = 'Date Served To is required';
    }
    if (!item.dischargeType) {
      errors.dischargeType = 'Type of Discharge is required';
    }

    if (item.dateServedFrom && item.dateServedTo) {
      const fromDate = new Date(item.dateServedFrom);
      const toDate = new Date(item.dateServedTo);
      if (fromDate > toDate) {
        errors.dateServedTo = MILITARY_DATE_RANGE_ERROR;
      }
    }

    return errors;
  }, []);

  const isDraftEmpty = useCallback((draft: MilitaryExperienceEntry): boolean => {
    return !draft.branch && !draft.dateServedFrom && !draft.dateServedTo && !draft.dischargeType;
  }, []);

  const renderEditForm = useCallback(
    (item: MilitaryExperienceEntry, onChange: (item: MilitaryExperienceEntry) => void, errors: FormListFieldError) => {
      const handleFieldChange = <K extends keyof MilitaryExperienceEntry>(
        field: K,
        value: MilitaryExperienceEntry[K],
      ) => {
        onChange({
          ...item,
          [field]: value,
        });
      };

      return (
        <div
          className='form-list__nested-form'
          data-testid={`${baseTestId}-service-form`}>
          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-service-branch`}>
            <Dropdown
              required
              data-testid={`${baseTestId}-service-branch-dropdown`}
              errorMessage={errors.branch}
              label='Branch of the Military'
              name='branch'
              options={militaryBranchOptions}
              value={item.branch || ''}
              onChange={(value) => handleFieldChange('branch', value)}
            />
          </div>

          <div className='form-list__inline-fields'>
            <div
              className='ds-u-margin-bottom--3 form-list__field-row'
              data-testid={`${baseTestId}-service-dates`}>
              <DatePicker
                required
                className='ds-u-margin-right--3'
                data-testid={`${baseTestId}-service-from`}
                errorMessage={errors.dateServedFrom}
                fromDate={minDate}
                id='dateServedFrom'
                label='Date Served From'
                name='dateServedFrom'
                toDate={today}
                tooltipText='Enter the date you started serving in this branch of the military'
                value={item.dateServedFrom || ''}
                onDateChange={(date, formattedDate) => handleFieldChange('dateServedFrom', formattedDate || '')}
              />
              <DatePicker
                required
                data-testid={`${baseTestId}-service-to`}
                errorMessage={errors.dateServedTo}
                fromDate={minDate}
                id='dateServedTo'
                label='Date Served To'
                name='dateServedTo'
                toDate={today}
                tooltipText='Enter the date you stopped serving in this branch of the military'
                value={item.dateServedTo || ''}
                onDateChange={(date, formattedDate) => handleFieldChange('dateServedTo', formattedDate || '')}
              />
            </div>
          </div>

          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-service-discharge`}>
            <Dropdown
              required
              data-testid={`${baseTestId}-service-discharge-dropdown`}
              errorMessage={errors.dischargeType}
              label='Type of Discharge'
              name='dischargeType'
              options={dischargeTypeOptions}
              value={item.dischargeType || ''}
              onChange={(value) => handleFieldChange('dischargeType', value)}
            />
          </div>
        </div>
      );
    },
    [minDate, today],
  );

  const renderItemSummary = useCallback((item: MilitaryExperienceEntry) => {
    const branchLabel = militaryBranchOptions.find((opt) => opt.value === item.branch)?.label || item.branch;
    const dischargeLabel =
      dischargeTypeOptions.find((opt) => opt.value === item.dischargeType)?.label || item.dischargeType;

    return {
      details: [`${item.dateServedFrom} - ${item.dateServedTo}`, dischargeLabel],
      title: branchLabel,
    };
  }, []);

  const renderReadOnlyItem = useCallback((item: MilitaryExperienceEntry) => {
    const branchLabel = militaryBranchOptions.find((opt) => opt.value === item.branch)?.label || item.branch;
    const dischargeLabel =
      dischargeTypeOptions.find((opt) => opt.value === item.dischargeType)?.label || item.dischargeType;

    return (
      <div className='form-list__nested-form'>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Branch of the Military</span>
          <span className='form-list__readonly-value'>{branchLabel}</span>
        </div>
        <div className='form-list__field-row'>
          <div className='form-list__readonly-field'>
            <span className='form-list__readonly-label'>Date Served From</span>
            <span className='form-list__readonly-value'>{item.dateServedFrom}</span>
          </div>
          <div className='form-list__readonly-field'>
            <span className='form-list__readonly-label'>Date Served To</span>
            <span className='form-list__readonly-value'>{item.dateServedTo}</span>
          </div>
        </div>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Type of Discharge</span>
          <span className='form-list__readonly-value'>{dischargeLabel}</span>
        </div>
      </div>
    );
  }, []);

  const draftFieldErrors: FormListFieldError = useMemo(() => {
    const draft = data.draftMilitaryExperience || getEmptyMilitaryExperience();
    let dateServedToError = fieldErrors?.['draftMilitaryExperience.dateServedTo'];

    if (draft.dateServedFrom && draft.dateServedTo) {
      const fromDate = new Date(draft.dateServedFrom);
      const toDate = new Date(draft.dateServedTo);
      if (fromDate > toDate) {
        dateServedToError = MILITARY_DATE_RANGE_ERROR;
      }
    }

    return {
      branch: fieldErrors?.['draftMilitaryExperience.branch'],
      dateServedFrom: fieldErrors?.['draftMilitaryExperience.dateServedFrom'],
      dateServedTo: dateServedToError,
      dischargeType: fieldErrors?.['draftMilitaryExperience.dischargeType'],
    };
  }, [data.draftMilitaryExperience, fieldErrors]);

  const showMilitaryServiceSection = data.hasMilitaryExperience === true;

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-has-military-experience`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-has-military-experience-dropdown`}
          errorMessage={fieldErrors?.hasMilitaryExperience}
          label='Have you ever served in the United States military?'
          name='hasMilitaryExperience'
          options={yesNoOptions}
          tooltipText='Select whether you have any military service history.'
          value={data.hasMilitaryExperience}
          onChange={handleMilitaryExperienceChange}
        />
      </div>

      {showMilitaryServiceSection && (
        <div
          className='ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-service-list`}>
          <FormList<MilitaryExperienceEntry>
            addLinkText='Add Another Military Service'
            data-testid={`${baseTestId}-service-form-list`}
            draftItem={data.draftMilitaryExperience || getEmptyMilitaryExperience()}
            fieldErrors={draftFieldErrors}
            getEmptyItem={getEmptyMilitaryExperience}
            isDraftActive={data.isAddingEntry}
            isDraftEmpty={isDraftEmpty}
            items={data.militaryExperiences || []}
            label='Military Service'
            renderEditForm={renderEditForm}
            renderItemSummary={renderItemSummary}
            renderReadOnlyItem={renderReadOnlyItem}
            tooltipText='Enter details about each period of military service you have completed.'
            validateItem={validateMilitaryExperience}
            onDraftActiveChange={handleDraftActiveChange}
            onDraftChange={handleDraftChange}
            onItemsChange={handleItemsChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Military Experience'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Military Service Details</h3>
          <p>
            If you have served in the military, please provide details about your service. This is required for the
            Declaration of Federal Employment (OF-306).
          </p>

          <h4 className='ds-u-margin-top--3'>Have you ever served in the United States military?</h4>
          <p>
            Select "Yes" if you have ever served in any branch of the United States Armed Forces. This includes active
            duty, reserve, and National Guard service. Types of service may include:
          </p>
          <ul>
            <li>Army</li>
            <li>Navy</li>
            <li>Air Force</li>
            <li>Marine Corps</li>
            <li>Coast Guard</li>
            <li>Space Force</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Branch of the Military</h4>
          <p>Select the branch of the military in which you served.</p>

          <h4 className='ds-u-margin-top--3'>Date Served From</h4>
          <p>
            Enter the date when you started your service in each branch. If you served more than once in the same
            branch, use the "Add Another Military Service" button to add each service period.
          </p>

          <h4 className='ds-u-margin-top--3'>Date Served To</h4>
          <p>
            Enter the date when you ended your service in each branch. If you served more than once in the same branch,
            use the "Add Another Military Service" button to add each service period.
          </p>

          <h4 className='ds-u-margin-top--3'>Type of Discharge</h4>
          <p>Select the type of discharge you received. Common types of discharge may include:</p>
          <ul>
            <li>Honorable</li>
            <li>General (Under Honorable Conditions)</li>
            <li>Other Than Honorable</li>
          </ul>

          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> If you served in the same branch more than once or in more than one branch, use the
            "Add Another Military Service" button to add each service period.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default MilitaryExperiencePage;
